import { save } from "@tauri-apps/plugin-dialog";
import { tauriOnlyInvoke } from "../invoke";
import { isNothing, just, nothing, type Maybe } from "../maybe";
import { saveDialogDefaultPath } from "../dialogDefaults";
import { resolveHomeDir } from "./dialogHome";

/** Save-dialog cancel and an empty path are both "the user backed out". */
function chosenSavePath(path: string | null): Maybe<string> {
  if (path === null || path === "") return nothing;
  return just(path);
}

/** Native PDF from a PdfBook JSON payload (#289). */
export async function exportBookPdfNative(
  bookJson: string,
  outputPath: string,
): Promise<void> {
  await tauriOnlyInvoke("export_book_pdf_native", {
    bookJson,
    outputPath,
  });
}

/** Prompt for a PDF path, then write a native PdfBook PDF. Nothing if cancelled. */
export async function saveBookPdfNative(
  bookJson: string,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.pdf$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Book";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export PDF",
      defaultPath: saveDialogDefaultPath(home, `${base}.pdf`),
      filters: [{ name: "PDF", extensions: ["pdf"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await exportBookPdfNative(bookJson, path.value);
  return path;
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunk = 0x8000;
  const stepCount = Math.ceil(bytes.length / chunk);
  const binary = Array.from({ length: stepCount }, (_, index) => {
    const start = index * chunk;
    return String.fromCharCode(...bytes.subarray(start, start + chunk));
  }).join("");
  return btoa(binary);
}

/** Write raw bytes to an absolute path (base64 over the IPC boundary). */
export async function writeBinaryFile(
  path: string,
  bytes: Uint8Array,
): Promise<void> {
  await tauriOnlyInvoke("write_binary_file", {
    path,
    dataBase64: bytesToBase64(bytes),
  });
}

/** Prompt for an EPUB path and write the package. Nothing if cancelled. */
export async function saveBookEpub(
  bytes: Uint8Array,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.epub$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Book";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export EPUB",
      defaultPath: saveDialogDefaultPath(home, `${base}.epub`),
      filters: [{ name: "EPUB", extensions: ["epub"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await writeBinaryFile(path.value, bytes);
  return path;
}

/** Prompt for a PNG path and write map image bytes. Nothing if cancelled. */
export async function saveMapPng(
  bytes: Uint8Array,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.(png|zwmap)$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Map";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export map image",
      defaultPath: saveDialogDefaultPath(home, `${base}.png`),
      filters: [{ name: "PNG image", extensions: ["png"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await writeBinaryFile(path.value, bytes);
  return path;
}

/** Prompt for an SVG path and write map SVG text. Nothing if cancelled. */
export async function saveMapSvg(
  svg: string,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.(svg|zwmap)$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Map";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export map SVG",
      defaultPath: saveDialogDefaultPath(home, `${base}.svg`),
      filters: [{ name: "SVG image", extensions: ["svg"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await writeBinaryFile(path.value, new TextEncoder().encode(svg));
  return path;
}

/** Prompt for a DOCX path and write the package. Nothing if cancelled. */
export async function saveBookDocx(
  bytes: Uint8Array,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.docx$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Book";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export DOCX",
      defaultPath: saveDialogDefaultPath(home, `${base}.docx`),
      filters: [{ name: "Word Document", extensions: ["docx"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await writeBinaryFile(path.value, bytes);
  return path;
}

/** Prompt for a Markdown path and write the manuscript. Nothing if cancelled. */
export async function saveBookMarkdown(
  markdown: string,
  defaultName: string,
): Promise<Maybe<string>> {
  const trimmed = defaultName.replace(/\.(md|markdown)$/i, "").trim();
  const base = trimmed !== "" ? trimmed : "Book";
  const home = await resolveHomeDir();
  const path = chosenSavePath(
    await save({
      title: "Export Markdown",
      defaultPath: saveDialogDefaultPath(home, `${base}.md`),
      filters: [{ name: "Markdown", extensions: ["md", "markdown"] }],
    }),
  );
  if (isNothing(path)) return nothing;
  await writeBinaryFile(path.value, new TextEncoder().encode(markdown));
  return path;
}
